import { type FC, useEffect, useRef } from "react";
import { Button, CircularProgress, IconButton, TextField } from "@mui/material";
import { type MessageResult } from "../../api/generated/data-contracts";
import { formatDayTime } from "../../utils/formatTime";
import { useNavigate } from "react-router-dom";
import { useTypedSelector } from "../../redux/store";
import { useAuth0 } from "@auth0/auth0-react";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import DeleteIcon from "@mui/icons-material/Delete";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import classNames from "classnames";

import "./styles.scss";
import { ADMIN_AUTH_ID, ADMIN_AUTH_ID1 } from "../HeaderTabs";

interface Props {
  messages: MessageResult[];
  isLoading: boolean;
  chatTitle: string;
  handleSendMessage: (message: string) => void;
  handleDelete: (messageId: number) => void;
  isGlobal: boolean;
}

export const Chat: FC<Props> = ({
  messages,
  isLoading,
  chatTitle,
  handleSendMessage,
  handleDelete,
  isGlobal
}) => {
  const navigate = useNavigate();
  const currentUser = useTypedSelector((state) => state.auth.currentUser);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const inputMessageRef = useRef<HTMLInputElement | null>(null);
  const { user } = useAuth0();

  const scrollToBottom = (): void => {
    setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop =
          scrollContainerRef.current.scrollHeight;
      }
    }, 1);
  };

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  const isMe = (senderId: string): boolean =>
    senderId === currentUser?.auth0Id || senderId === user?.sub;

  const handleSenderClick =
    (interlocutorId: string): VoidFunction =>
    () => {
      navigate(`/chat/${interlocutorId}`);
    };

  const handleSend = (): void => {
    const message = inputMessageRef.current?.value.trim();

    if (message && inputMessageRef.current) {
      handleSendMessage(message);
      scrollToBottom();
      inputMessageRef.current.value = "";
    }
  };

  const sortedMessages = [...messages].sort(
    (a, b) => a.createdTimestamp - b.createdTimestamp
  );

  return (
    <>
      {isLoading ? (
        <div className="center-loading-block">
          <CircularProgress />
        </div>
      ) : (
        <div className="chat">
          <div className="chat-header">
            <IconButton
              color="inherit"
              onClick={() => {
                navigate("/chat");
              }}
            >
              <ArrowBackIosNewRoundedIcon />
            </IconButton>

            <span>{chatTitle}</span>
          </div>

          <div className="chat-messages" ref={scrollContainerRef}>
            {sortedMessages.map((message) => (
              <div
                key={message.uuid}
                className={classNames("chat-messages__item", {
                  "chat-messages__item-me": isMe(message.sender.auth0Id)
                })}
              >
                <div className="chat-message__content">
                  <div className="chat-message__meta">
                    <span
                      className="chat-message__sender"
                      onClick={handleSenderClick(message.sender.auth0Id)}
                    >
                      {message.sender.fullName ??
                        message.sender.userName ??
                        "N/A"}
                    </span>

                    <span className="chat-message__time">
                      {formatDayTime(message.createdTimestamp)}
                    </span>
                  </div>

                  <div
                    className={classNames("chat-message__text", {
                      "chat-message__text-me": isMe(message.sender.auth0Id),
                      "chat-message__text-sending": message.id === 0
                    })}
                  >
                    <span className="chat-message__text-content">
                      {message.content}
                    </span>

                    {(isGlobal
                      ? currentUser?.auth0Id === ADMIN_AUTH_ID ||
                        currentUser?.auth0Id === ADMIN_AUTH_ID1
                      : isMe(message.sender.auth0Id)) && (
                      <IconButton
                        onClick={() => {
                          handleDelete(message.id);
                        }}
                        sx={{
                          color: "black",
                          padding: 0,
                          marginLeft: "10px"
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="chat-input-container">
            <div className="chat-input-wrapper">
              <TextField
                type="text"
                inputRef={inputMessageRef}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSend();
                  }
                }}
                placeholder="Type your message..."
                fullWidth
                className="chat-input"
                autoComplete="off"
              />
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleSend}
                className="chat-send-button"
              >
                <SendRoundedIcon />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
